<script lang="ts">
	import HugeHeading from "@components/HugeHeading.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import { hugeHeadingParallax } from "@utils/hugeHeadingParallax.js";
	import type { ClassValue } from "svelte/elements";
	import chart from "@core/assets/icons/chart.svg?raw";
	import handshake from "@core/assets/icons/handshake.svg?raw";
	import helmet from "@core/assets/icons/helmet.svg?raw";
	import logistics from "@core/assets/icons/logistics.svg?raw";
	import security from "@core/assets/icons/security.svg?raw";
	import team from "@core/assets/icons/team.svg?raw";

	const { class: customClass }: { class?: ClassValue } = $props();

	const reasons: [icon: string, title: string, description: string][] = [
		[
			logistics,
			"Známe reálný provoz",
			"Máme zkušenosti s reálnými projekty a řízením týmů ve firmách jako Grizly, Sportisimo nebo OBI. Víme, co dělat, když něco nefunguje, a naše doporučení vycházejí z praxe, ne od stolu.",
		],
		[
			team,
			"Specialisté, kteří tvoří tým",
			"Nejsme jeden konzultant, ale tým odborníků na logistiku, data, obaly, HR nebo technologie. Každý projekt řeší náš vlastní mini-tým, takže dokážeme pokrýt celý řetězec od procesů po lidi.",
		],
		[
			helmet,
			"Pracujeme v terénu",
			"Když navrhneme řešení, hned ho uvedeme do praxe. Stojíme s vámi ve skladu, měříme, školíme a pomáháme lidem pochopit změny, aby se logistika opravdu měnila k lepšímu.",
		],
		[
			chart,
			"Rychlé a měřitelné výsledky",
			"Nehledáme výmluvy, ale konkrétní řešení. Měříme jejich dopad na úspory, výkon a náklady. První výsledky naši klienti většinou vidí během několika týdnů.",
		],
		[
			security,
			"Férovost a žádné triky",
			"Neprodáváme iluze a hned na začátku říkáme, co dává smysl a co ne. Ceny, harmonogram i výstupy jsou vždy transparentní, bez skrytých položek nebo marketingových triků.",
		],
		[
			handshake,
			"Dlouhodobá partnerství",
			"S mnoha klienty spolupracujeme dlouhodobě a pomáháme jim růst, rozvíjet tým, plánovat nové sklady a digitalizovat provoz.",
		],
	];
</script>

<!--
	[&:is(*)] is used to overwrite full-width's styles by increasing specificity.
 	!important isn't suitable, as we accept `customClass` and thus padding should be overridable
-->
<section class={[customClass, "full-width [&:is(*)]:px-4 md:[&:is(*)]:px-8 max-w-[120rem] mx-auto flex flex-col"]}>
	<div class="flex flex-col 2xl:flex-row">
		<!-- !mb-0 cancels the negative margin-bottom not to break vertical alignment in the flexbox -->
		<HugeHeading level={2} text="stroke[[Proč se do toho]] nl[[]] pustit s námi" class="2xl:grow !mb-0" />
		<div
			class="2xl:w-1/3 max-w-[34.75rem] shrink-0 2xl:px-8 2xl:flex flex-col justify-center items-center 2xl:self-stretch"
		>
			<p class="my-4 md:my-8 2xl:my-0 font-semibold text-xs/[1.4] md:text-[1.25rem]/[1.4]">
				<Formatted
					text="Spolupracujte s týmem, který má s logistikou reálné zkušenosti, uvádí návrhy do praxe a přináší měřitelné výsledky."
				/>
			</p>
		</div>
	</div>
	<div class="huge-heading" use:hugeHeadingParallax>
		<!-- This will apply the negative margin-bottom which we need to cancel above -->
	</div>
	<div class="grid md:grid-cols-2 2xl:grid-cols-3 gap-2 items-stretch w-fit mx-auto">
		{#each reasons as [icon, title, description], index (index)}
			<div class="bg-white/50 backdrop-blur-lg rounded-xl md:rounded-3xl p-4 flex flex-col max-w-[33rem]">
				<div class="flex gap-2 md:gap-12 lg:gap-16 md:flex-col items-center md:items-start mb-3">
					<div class="size-8 md:size-16 lg:size-24 bg-cmcprimary rounded-lg flex items-center justify-center">
						<Icon class="text-white size-6 md:size-12" {icon} />
					</div>
					<h2
						class="text-cmcsecondary uppercase font-druk-cond text-2xl/[0.9] md:text-[2.5rem]/[0.9] lg:text-5xl/[0.9]"
					>
						<Formatted text={title} />
					</h2>
				</div>
				<p class="font-medium text-2xs/[1.5] md:text-base mb-4">
					<Formatted text={description} />
				</p>
			</div>
		{/each}
	</div>
</section>
